import { day_of_week } from "@/models/daily-work.model";
import { monthly_schedule_occurrence, schedule_assignment_kind } from "@/models/daily-work-schedule.model";

/** Matches Postgres extract(dow): 0 = Sunday .. 6 = Saturday. */
export const dayToIndex: Record<day_of_week, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const indexToDay: Record<number, day_of_week> = Object.fromEntries(
  Object.entries(dayToIndex).map(([day, index]) => [index, day as day_of_week])
);

export const dayOfWeekLabels: Record<day_of_week, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

/** Monday-first ordering for the weekly board. */
export const orderedWeekdays: day_of_week[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const monthlyOccurrenceLabels: Record<monthly_schedule_occurrence, string> = {
  first: "1st",
  second: "2nd",
  third: "3rd",
  fourth: "4th",
  last: "Last",
};

export function createLocalId(prefix = "ms"): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export interface AssignmentSubject {
  assignment_kind: schedule_assignment_kind;
  assigned_user?: string | null;
  assigned_org?: string | null;
}

/**
 * Who a staffing entry points at, independent of its id. Mirrors the partial
 * unique indexes on daily_work_schedule_assignments (one active entry per
 * (row, user) and per (row, org)).
 */
export function assignmentSubjectKey(input: AssignmentSubject): string {
  return input.assignment_kind === "individual"
    ? `user:${input.assigned_user ?? ""}`
    : `org:${input.assigned_org ?? ""}`;
}

/** Subject keys that appear more than once (entries with no subject are ignored). */
export function findDuplicateAssignmentSubjects(inputs: AssignmentSubject[]): string[] {
  const counts = inputs.reduce<Map<string, number>>((acc, input) => {
    const hasSubject = input.assignment_kind === "individual" ? Boolean(input.assigned_user) : Boolean(input.assigned_org);
    if (!hasSubject) return acc;
    const key = assignmentSubjectKey(input);
    return new Map(acc).set(key, (acc.get(key) ?? 0) + 1);
  }, new Map());
  return [...counts.entries()].filter(([, count]) => count > 1).map(([key]) => key);
}

export type MaxHoursParseResult = { ok: true; value: number | null } | { ok: false; error: string };

/** Blank = no limit; otherwise must be > 0 (daily_work_schedule_rows_max_hours_check). */
export function parseMaxHoursInput(raw: string): MaxHoursParseResult {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, value: null };
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) {
    return { ok: false, error: "Max hours must be a number greater than 0 (leave blank for no limit)" };
  }
  return { ok: true, value };
}
